import { NumberUtils, useShowNotification } from '@elotech/components';
import { useLoading } from 'common/hooks';
import { ArquivoService } from 'common/service';
import { ArquivoEntity, ArquivoUrl, AssinaturaDigital } from 'common/type';
import { FilePathEnum, FileSizeEnum, MimeType } from 'common/type/enum';
import saveAs from 'file-saver';
import {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Accept } from 'react-dropzone';

import { Informativo } from '../informativo';
import { TransProgressKind, useTransProgress } from '../transference-progress';
import { ArquivoContext } from './ArquivoContext';
import {
  ArquivoPropsComponent,
  ArquivoPropsComponentRefType,
  initialArquivoPropsComponentRef
} from './ArquivoPropsComponent';
import {
  ArquivoUtils,
  getFileName,
  isHtmlFile,
  updateEntityWithPatches
} from './arquivoUtils';
import { PdfSignatureFile, checkPdfSignatures } from './checkPdfSignatures';
import DragDropFiles from './DragDropFiles';
import type {
  OnVisualizarArquivoHtml,
  TemplateEditingAction,
  TemplateEditingResult
} from './templateEditingTypes';

export type ArquivoContext = {
  reloadFiles: () => Promise<void>;
};

type Props = {
  id?: string;
  maxFileCount?: number;
  byTipo?: string;
  canDelete?: boolean | ((file: ArquivoEntityWithTemp) => boolean);
  canView?: boolean | ((file: ArquivoEntityWithTemp) => boolean);
  canDownload?: boolean | ((file: ArquivoEntityWithTemp) => boolean);
  canUpload?: boolean;
  isLoading?: boolean;
  service: ArquivoService;
  filePath?: FilePathEnum;
  onVisualizarArquivoHtml?: OnVisualizarArquivoHtml;
  extraButton?: (file: ArquivoEntity) => any;
  labels?: (file: ArquivoEntity) => any;
  maxFileNameLength?: number;
  showArquivoProps?: boolean;
  children?: ReactNode;
  getExtraCaption?(file: ArquivoEntity): any;
  afterSave?: (arquivoEntity: ArquivoEntity) => void;
  afterDelete?: () => void;
  onFinish?: () => void;
  acceptAll?: boolean;
  enforceFileSizeLimit?: boolean;
};

export type ArquivoComponentRef = {
  saveArquivosTemporarios: (domainEntityId: string) => Promise<void>;
  reload: () => Promise<void>;
  arquivos: ArquivoEntityWithTemp[];
};

export type ArquivoEntityWithTemp = Omit<ArquivoEntity, 'domainEntity'> & {
  temporario?: boolean;
  localFile?: File;
  domainEntity?: ArquivoEntity['domainEntity'];
};

const acceptedFiles = {
  [MimeType.APPLICATION_PDF]: ['.pdf'],
  [MimeType.IMAGE_JPEG]: ['.jpg', '.jpeg'],
  [MimeType.IMAGE_PNG]: ['.png'],
  [MimeType.APLICATION_DOCX]: ['.docx'],
  [MimeType.APPLICATION_MSWORD]: ['.doc'],
  [MimeType.TEXT_PLAIN]: ['.txt'],
  [MimeType.APLICATION_XLS]: ['.xls'],
  [MimeType.APLICATION_XLSX]: ['.xlsx'],
  [MimeType.APPLICATION_ZIP]: ['.zip'],
  [MimeType.APPLICATION_RAR]: ['.rar'],
  [MimeType.APPLICATION_ODT]: ['.odt'],
  [MimeType.APPLICATION_ODS]: ['.ods'],
  [MimeType.APPLICATION_OTS]: ['.ots'],
  [MimeType.IMAGE_BMP]: ['.bmp'],
  [MimeType.IMAGE_GIF]: ['.gif'],
  [MimeType.APPLICATION_PPT]: ['.pot', '.ppa', '.pps', '.ppt', '.pwz'],
  [MimeType.APPLICATION_PPTX]: ['.pptx'],
  [MimeType.APPLICATION_RTF]: ['.rtf']
} as Accept;

const asTemplateEditingAction = (
  result: TemplateEditingResult
): TemplateEditingAction | undefined => {
  if (typeof result === 'string') {
    return { action: 'update', template: result };
  }
  if (result && (result.action === 'update' || result.action === 'create')) {
    return result;
  }
  return undefined;
};

const ArquivoComponent = forwardRef<ArquivoComponentRef, Props>(
  (props, ref) => {
    const {
      id,
      byTipo,
      service,
      canDelete = true,
      canView = true,
      canDownload = true,
      canUpload = true,
      isLoading = false,
      onVisualizarArquivoHtml,
      filePath = FilePathEnum.COMPRAS_ATOS,
      extraButton,
      labels,
      maxFileNameLength,
      showArquivoProps = false,
      children,
      getExtraCaption,
      afterSave,
      afterDelete,
      onFinish,
      acceptAll = false,
      enforceFileSizeLimit = true
    } = props;

    const fileHandlerRef = useRef<ArquivoPropsComponentRefType>(
      initialArquivoPropsComponentRef
    );

    const [maxSize, setMaxSize] = useState<number>(
      FileSizeEnum.TAMANHO_ARQUIVO_DEFAULT
    );
    const [loading, setLoading] = useLoading();
    const [checkingSignatures, checkSignatures] = useLoading();
    const [arquivosEntity, setArquivosEntity] = useState<
      ArquivoEntityWithTemp[]
    >([]);

    const processingFiles = useRef<number>(0);
    const uploadingFiles = useRef<number>(0);

    const showNotification = useShowNotification();
    const utils = ArquivoUtils(service, filePath);

    const aplicarAssinaturasDigitais = (
      assinaturasPorArquivo: Record<string, AssinaturaDigital[]>
    ) => {
      setArquivosEntity(arquivos =>
        arquivos.map(arquivoEntity => {
          const arquivoEntityId = arquivoEntity.id
            ? String(arquivoEntity.id)
            : undefined;

          if (!arquivoEntityId) {
            return arquivoEntity;
          }

          const assinaturasDigitais =
            assinaturasPorArquivo[arquivoEntityId] || [];

          return {
            ...arquivoEntity,
            arquivo: {
              ...arquivoEntity.arquivo,
              possuiAssinaturaDigital: assinaturasDigitais.length > 0,
              assinaturasDigitais
            }
          };
        })
      );
    };

    const verificarAssinaturasDigitais = async (entityId: string) => {
      try {
        const request = byTipo
          ? service.verificarAssinaturasDigitaisPorTipo?.(entityId, byTipo)
          : service.verificarAssinaturasDigitais?.(entityId);

        if (!request) {
          return;
        }

        const { data } = await checkSignatures<{
          data: Record<string, AssinaturaDigital[]>;
        }>(request);

        aplicarAssinaturasDigitais(data);
      } catch {
        showNotification({
          level: 'error',
          message: 'Erro ao verificar assinaturas digitais'
        });
      }
    };

    const getArquivos = async (entityId: string) => {
      const request = byTipo
        ? service.buscarArquivosPorTipo(entityId, byTipo)
        : service.buscarArquivos(entityId);
      const { data: arquivos } = await setLoading<{
        data: ArquivoEntityWithTemp[];
      }>(request);

      setArquivosEntity(arquivos);

      if (arquivos.length > 0) {
        void verificarAssinaturasDigitais(entityId);
      }
    };

    const saveFile = ({
      file,
      entityId,
      waitSaveEntity,
      patches
    }: {
      file: File;
      entityId: string;
      waitSaveEntity?: boolean;
      patches?: any;
    }) => {
      const fileNameAccent = getFileName(patches, file);
      const fileNameUnaccent = utils.getUnaccentFileName(fileNameAccent);
      processingFiles.current++;
      uploadingFiles.current++;

      const assinaturaDigital =
        (file as PdfSignatureFile).hasSignatures === undefined
          ? checkPdfSignatures(file)
          : Promise.resolve(!!(file as PdfSignatureFile).hasSignatures);

      const saveEntityFn = Promise.all([
        utils.saveFileToEntity({
          file: file,
          entityId: entityId,
          fileName: fileNameUnaccent,
          patches
        }),
        assinaturaDigital
      ])
        .then(([{ data }, possuiAssinaturaDigital]) => {
          const arquivoSalvo = {
            ...data,
            arquivo: {
              ...data.arquivo,
              possuiAssinaturaDigital
            }
          };

          setArquivosEntity(prevState => [...prevState, arquivoSalvo]);
          afterSave?.(arquivoSalvo);
        })
        .catch(error =>
          showNotification({
            level: 'error',
            message: `Não foi possível salvar o arquivo. - ${
              error.response?.data?.message ||
              error.response?.data ||
              error.message
            }`
          })
        )
        .finally(() => {
          processingFiles.current--;
          uploadingFiles.current--;
          if (uploadingFiles.current === 0) {
            if (!waitSaveEntity) {
              void verificarAssinaturasDigitais(entityId);
            }
            onFinish?.();
          }
        });

      return waitSaveEntity ? saveEntityFn : Promise.resolve();
    };

    const onAdicionarArquivo = (file: File) => {
      const fileNameUnaccent = utils.getUnaccentFileName(file.name);
      const arquivoEncontrado = arquivosEntity.some(
        ({ arquivo }) => arquivo.nome === fileNameUnaccent
      );

      if (arquivoEncontrado) {
        return showNotification({
          level: 'error',
          message: 'Arquivo já inserido.'
        });
      }

      if (
        props.maxFileCount &&
        (processingFiles.current || arquivosEntity.length) >= props.maxFileCount
      ) {
        return showNotification({
          level: 'error',
          message: `Só é possível enviar ${props.maxFileCount} arquivo(s).`
        });
      }

      return finalizarAddArquivo(file);
    };

    const finalizarAddArquivo = async (file: File) => {
      const patches =
        (await fileHandlerRef.current?.addFile(file, {}, true)) || {};

      if (!id) {
        const possuiAssinaturaDigital = await checkPdfSignatures(file);
        const arquivoEntity = utils.arquivoUrlToArquivoEntity(
          {
            fullPath: URL.createObjectURL(file)
          },
          file,
          patches
        );

        return setArquivosEntity(prevState => [
          ...prevState,
          {
            ...arquivoEntity,
            arquivo: {
              ...arquivoEntity.arquivo,
              possuiAssinaturaDigital
            }
          }
        ]);
      }

      return setLoading(
        saveFile({
          file,
          entityId: id,
          patches
        })
      );
    };

    const onDeleteArquivo = (
      arquivoEntity: ArquivoEntityWithTemp,
      index: number
    ) => {
      const deleteFn = (id: string) => {
        if (arquivoEntity.temporario) return Promise.resolve();
        return service.deletarArquivo(id);
      };

      setLoading(
        deleteFn(arquivoEntity.id!)
          .then(() => {
            setArquivosEntity([
              ...arquivosEntity.slice(0, index),
              ...arquivosEntity.slice(index + 1)
            ]);
            afterDelete && afterDelete();
          })
          .catch(({ response }) =>
            showNotification({
              level: 'error',
              message: `Não foi possível deletar o arquivo. - ${
                response.data.message || response.data
              }`
            })
          )
      );
    };

    const onAtualizarArquivo = async (arquivoEntity: any, index: number) => {
      const updateEntity = arquivoEntityAtualizado =>
        setArquivosEntity([
          ...arquivosEntity.slice(0, index),
          arquivoEntityAtualizado,
          ...arquivosEntity.slice(index + 1)
        ]);

      const patches = await fileHandlerRef.current?.addFile(
        arquivoEntity.localFile || new File([], arquivoEntity.arquivo.nome!),
        arquivoEntity,
        false
      );

      const arquivoEntityBefore = { ...arquivoEntity };

      updateEntityWithPatches(arquivoEntity, patches);

      if (!id || arquivoEntity.temporario) {
        updateEntity(arquivoEntity);
        return;
      }

      setLoading(
        service
          .atualizarArquivo(id!, arquivoEntity)
          .then(({ data }) => updateEntity(data))
          .catch(({ response }) => {
            showNotification({
              level: 'error',
              message: `Não foi possível atualizar o arquivo. - ${
                response.data.message || response.data
              }`
            });
            updateEntity(arquivoEntityBefore);
          })
      );
    };

    const onVisualizarArquivo = (
      _index: number,
      arquivoEntity: ArquivoEntityWithTemp
    ) => {
      const { arquivo, temporario } = arquivoEntity;
      const isHtml = isHtmlFile(arquivo);

      if (!arquivo.fullPath) return;

      if (temporario) {
        if (isHtml && onVisualizarArquivoHtml) {
          return onViewHtmlFile(
            {
              dataCriacao: arquivo.dataCriacao || new Date().toISOString(),
              fullPath: arquivo.fullPath,
              urlAssinada: arquivo.fullPath
            },
            arquivoEntity
          );
        }

        return onDownloadArquivo({ arquivo, temporario });
      }

      setLoading(
        service
          .gerarUrlParaDownload(arquivo.fullPath!, arquivo.nome!)
          .then(({ data: url }) => {
            if (isHtml && onVisualizarArquivoHtml) {
              onViewHtmlFile(url, arquivoEntity);
              return;
            }

            if (
              arquivo.mimeType !== MimeType.APPLICATION_PDF &&
              !`${arquivo.mimeType}`.toLowerCase().includes('image/')
            ) {
              onDownloadArquivo({ arquivo });
              return;
            }

            service
              .receberArquivoDoServidor(
                url.urlAssinada,
                useTransProgress(
                  arquivo.nome || 'Arquivo sem nome',
                  TransProgressKind.PREVIEW
                )
              )
              .then(({ data }) => {
                const file = new Blob([data], { type: arquivo.mimeType });
                const fileURL = URL.createObjectURL(file);
                setTimeout(() => {
                  URL.revokeObjectURL(fileURL);
                }, 6e4);

                if (!window.open(fileURL)) {
                  throw new Error('Sem permissão para mostrar popups');
                }
              });
          })
      );
    };

    const onViewHtmlFile = async (
      url: ArquivoUrl,
      arquivoInicial: ArquivoEntityWithTemp
    ) => {
      const fileName = arquivoInicial?.arquivo?.nome || 'template.html';
      let arquivoAtual = arquivoInicial;
      let templateAtual = await fetch(url.urlAssinada).then(data =>
        data.text()
      );

      const replaceArquivo = (
        atual: ArquivoEntityWithTemp,
        atualizado: ArquivoEntityWithTemp
      ) => {
        setArquivosEntity(arquivos =>
          arquivos.map(arquivo => {
            const isSameArquivo = atual.id
              ? arquivo.id === atual.id
              : arquivo === atual;
            return isSameArquivo ? atualizado : arquivo;
          })
        );
      };

      const createFile = (template: string) => {
        const blob = new Blob([template], { type: MimeType.TEXT_HTML });
        return new File([blob], fileName, {
          type: blob.type,
          lastModified: new Date().getTime()
        });
      };

      const persistTemplate = async ({
        action,
        template
      }: TemplateEditingAction) => {
        const file = createFile(template);

        if (!id) {
          const novoArquivo = utils.arquivoUrlToArquivoEntity(
            { fullPath: URL.createObjectURL(file) },
            file
          );

          if (action === 'update') {
            replaceArquivo(arquivoAtual, novoArquivo);
          } else {
            setArquivosEntity(arquivos => [...arquivos, novoArquivo]);
          }
          return novoArquivo;
        }

        const novaUrl = await service
          .gerarUrlParaUpload(filePath, fileName, MimeType.TEXT_HTML)
          .then(({ data }) => data);

        await service.enviarArquivoParaServidor(
          file,
          novaUrl.urlAssinada,
          useTransProgress(fileName, TransProgressKind.UPLOAD)
        );

        if (action === 'update' && arquivoAtual.id) {
          const arquivoAtualizado = await service
            .atualizarArquivoUrl(id, arquivoAtual.id, novaUrl)
            .then(({ data }) => data);
          replaceArquivo(arquivoAtual, arquivoAtualizado);
          return arquivoAtualizado;
        }

        const novoArquivo = await service
          .salvarArquivoEntity(
            id,
            utils.arquivoUrlToArquivoEntity(novaUrl, file, id)
          )
          .then(({ data }) => data);
        setArquivosEntity(arquivos => [...arquivos, novoArquivo]);
        afterSave?.(novoArquivo);
        return novoArquivo;
      };

      while (onVisualizarArquivoHtml) {
        const result = await onVisualizarArquivoHtml(templateAtual, fileName);
        const editingAction = asTemplateEditingAction(result);
        if (!editingAction) {
          return;
        }

        arquivoAtual = await setLoading(persistTemplate(editingAction));
        templateAtual = editingAction.template;
      }
    };

    const onDownloadArquivo = ({
      arquivo: { fullPath, nome, mimeType },
      temporario
    }: Pick<ArquivoEntityWithTemp, 'arquivo' | 'temporario'>) => {
      if (!fullPath) return;

      const download = (url: string) =>
        utils
          .downloadArquivo({
            mimeType,
            nome,
            url
          })
          .then(blob => saveAs(blob, nome));

      if (temporario) {
        return download(fullPath);
      }

      setLoading(
        service
          .gerarUrlParaDownload(fullPath, nome || '')
          .then(({ data: url }) => download(url.urlAssinada))
      );
    };

    const onChangeSigilo = (file: ArquivoEntityWithTemp, index: number) => {
      if (file.temporario) {
        return setArquivosEntity(prevValues => {
          prevValues[index].sigiloso = !file.sigiloso;
          return [...prevValues];
        });
      }

      setLoading(
        service
          .alterarSigilo(file.id!)
          .then(response => {
            const newArquivosEntity = [...arquivosEntity];
            newArquivosEntity[index] = response.data;
            setArquivosEntity([...newArquivosEntity]);
          })
          .catch(({ response }) =>
            showNotification({
              level: 'error',
              message: `Não foi possível alterar o sigilo do arquivo. - ${
                response.data.message || response.data
              }`
            })
          )
      );
    };

    const handleSaveArquivosTemporarios = async (
      entityId: NonNullable<Props['id']>
    ) => {
      const arquivosTemporarios = arquivosEntity.filter(
        arquivo => arquivo.temporario && arquivo.localFile
      );

      return setLoading(
        Promise.all(
          arquivosTemporarios.map(arquivo =>
            saveFile({
              entityId,
              file: arquivo.localFile!,
              waitSaveEntity: true
            })
          )
        ).then(() => {
          getArquivos(entityId);
        })
      );
    };

    const handleExtraButton = (file: ArquivoEntityWithTemp) => {
      if (file.temporario) return;

      return extraButton?.(file as ArquivoEntity);
    };

    const handleLabels = (file: ArquivoEntityWithTemp) => {
      if (file.temporario) return;

      return labels?.(file as ArquivoEntity);
    };

    const clearNotTempFiles = () => {
      setArquivosEntity(prevValues => {
        return prevValues.filter(value => value.temporario);
      });
    };

    const onReorder = (files: ArquivoEntityWithTemp[]) => {
      service.reorder(files.map(file => file.id!));
    };

    useEffect(() => {
      setLoading(
        service.getMaxSize().then(({ data }) => {
          if (NumberUtils.isNumeric(data)) {
            setMaxSize(data);
          }
        })
      );
    }, [service, setLoading]);

    const reloadFiles = async () => {
      if (!id) {
        clearNotTempFiles();
        return;
      }
      getArquivos(id);
      return;
    };

    useEffect(() => {
      reloadFiles();
    }, [id, byTipo, service, setLoading]);

    useImperativeHandle(
      ref,
      () => ({
        saveArquivosTemporarios: handleSaveArquivosTemporarios,
        reload: () => reloadFiles(),
        arquivos: arquivosEntity
      }),
      [arquivosEntity]
    );

    const renderDragDropFiles = (showingFiles = false) => {
      const isAnyLoading = showingFiles && (loading || isLoading);

      const showEmptyInformativo =
        showingFiles && !arquivosEntity.length && !canUpload && !isAnyLoading;

      if (showEmptyInformativo) {
        return (
          <Informativo type="WARNING" withIcon>
            Nenhum arquivo encontrado.
          </Informativo>
        );
      }

      return (
        <DragDropFiles
          service={service}
          maxSize={maxSize}
          canDelete={canDelete}
          canView={canView}
          canDownload={canDownload}
          canUpload={canUpload}
          files={showingFiles ? arquivosEntity : []}
          setFiles={setArquivosEntity}
          onView={onVisualizarArquivo}
          acceptedFiles={acceptAll ? undefined : acceptedFiles}
          enforceFileSizeLimit={enforceFileSizeLimit}
          onAddFile={onAdicionarArquivo}
          canUpdate={showArquivoProps && !!id}
          onUpdateFile={onAtualizarArquivo}
          onRemoveFile={onDeleteArquivo}
          onDownload={onDownloadArquivo}
          onChangeSigilo={onChangeSigilo}
          loading={isAnyLoading}
          checkingSignatures={checkingSignatures}
          extraButton={handleExtraButton}
          labels={handleLabels}
          maxFileNameLength={maxFileNameLength}
          onReorder={onReorder}
          getExtraCaption={getExtraCaption}
          canEditHTML={!!onVisualizarArquivoHtml}
        />
      );
    };

    return (
      <ArquivoContext.Provider value={{ reloadFiles }}>
        {children && showArquivoProps && (
          <ArquivoPropsComponent
            renderDragDrop={renderDragDropFiles}
            ref={fileHandlerRef}
            labels={labels}
            showArquivoProps
          >
            {children}
          </ArquivoPropsComponent>
        )}
        {renderDragDropFiles(true)}
      </ArquivoContext.Provider>
    );
  }
);

export { ArquivoComponent, ArquivoComponent as default };
